import type { Task } from '@/types/task'
import { JWTHttpClient } from '@/services/JWTHttpClient'
import type { ExplicitTaskFilter } from '@/composables/services/taskFilter'

export type FilterValue = string | number | boolean | null | ExplicitTaskFilter

export interface TaskSearchRequest {
  filters?: Record<string, FilterValue>
  sort?: string
  pagination?: { limit: number; start: number }
}

export interface TaskSearchMeta {
  total: number
  filtered: number
  limit: number
  start: number
  hasMore: boolean
}

export interface TaskSearchResponse {
  data: Task[]
  meta: TaskSearchMeta
  links?: {
    self?: string
    next?: string
    prev?: string
  }
  filters?: Record<string, unknown>
}

export interface CreateTaskResponse {
  success: boolean
  createdTask?: Task
  task?: Task
}

// Get the tasks service URL from environment variable, fallback to stage API Gateway
const TASKS_SERVICE_URL = import.meta.env.VITE_TASKS_SERVICE_URL || '/service-tasks-stage'

// Create JWT HTTP client for Task Service API calls
const jwtHttpClient = new JWTHttpClient(TASKS_SERVICE_URL)

export async function fetchCaseTasks(caseId: string): Promise<Task[]> {
  try {
    const response = await jwtHttpClient.get<Task[]>('/v1/tasks', { 
      caseId: caseId 
    }, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:tasks'
    })
    return response
  } catch (error) {
    console.error('Failed to fetch case tasks:', error)
    throw new Error('Failed to fetch case tasks')
  }
}

export async function fetchUserTasks(userId: string): Promise<Task[]> {
  try {
    const response = await jwtHttpClient.get<Task[]>('/v1/tasks', { 
      userId: userId 
    }, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    return response
  } catch (error) {
    // Check if it's a 404 error (user not found or no tasks)
    if (error instanceof Error && error.message.includes('404')) {
      console.warn(`No tasks found for user ${userId} (404 - user may not exist or has no tasks)`)
      return [] // Return empty array instead of throwing
    }
    console.error('Failed to fetch user tasks:', error)
    throw new Error('Failed to fetch user tasks')
  }
}

export async function fetchTeamTasks(teamId: string): Promise<Task[]> {
  try {
    const response = await jwtHttpClient.get<Task[]>('/v1/tasks', { 
      teamId: teamId 
    }, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    return response
  } catch (error) {
    // Check if it's a 404 error (team not found or no tasks)
    if (error instanceof Error && error.message.includes('404')) {
      console.warn(`No tasks found for team ${teamId} (404 - team may not exist or has no tasks)`)
      return [] // Return empty array instead of throwing
    }
    console.error('Failed to fetch team tasks:', error)
    throw new Error('Failed to fetch team tasks')
  }
}

export async function fetchTaskById(taskId: string): Promise<Task> {
  try {
    const response = await jwtHttpClient.get<Task>(`/v1/tasks/${encodeURIComponent(taskId)}`, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    return response
  } catch (error) {
    console.error('Failed to fetch task:', error)
    throw new Error('Failed to fetch task')
  }
}

export async function updateTaskStatus(task: Task, status: string): Promise<Task> {
  try {
    // Only send the fields required by the backend
    const { id, name, description, dueDate, deleted, links } = task
    const updatedTask: any = {
      id,
      name,
      description,
      dueDate: dueDate && dueDate.trim() !== '' ? dueDate : null,
      deleted: deleted ?? false,
      links,
      status,
    }
    
    // Ensure we never send owner_team_id or owner_user_id at the root level
    // These should only exist within the links array
    delete updatedTask.owner_team_id
    delete updatedTask.owner_user_id
    
    const response = await jwtHttpClient.put<any>(`/v1/tasks/${encodeURIComponent(task.id)}`, updatedTask, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    
    return response.createdTask || response.task || response
  } catch (error) {
    console.error('Failed to update task status:', error)
    throw new Error('Failed to update task status')
  }
}

export async function updateTask(taskToUpdate: Task, updates: Partial<Task>): Promise<Task> {
  try {
    const { id, name, description, dueDate, deleted, links, status } = taskToUpdate
    
    // Prepare the updated task data, merging updates with existing data
    const updatedTask: any = {
      id,
      name: updates.name !== undefined ? updates.name : name,
      description: updates.description !== undefined ? updates.description : description,
      dueDate: updates.dueDate !== undefined 
        ? (updates.dueDate && updates.dueDate.trim() !== '' ? updates.dueDate : null)
        : (dueDate && dueDate.trim() !== '' ? dueDate : null),
      deleted: updates.deleted !== undefined ? updates.deleted : (deleted ?? false),
      links: updates.links !== undefined ? updates.links : links,
      status: updates.status !== undefined ? updates.status : status,
    }
    
    // Ensure we never send owner_team_id or owner_user_id at the root level
    // These should only exist within the links array
    delete updatedTask.owner_team_id
    delete updatedTask.owner_user_id
    
    const response = await jwtHttpClient.put<any>(`/v1/tasks/${encodeURIComponent(taskToUpdate.id)}`, updatedTask, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:tasks'
    })
    
    return response.createdTask || response.task || response
  } catch (error) {
    console.error('Failed to update task:', error)
    throw new Error('Failed to update task')
  }
}

export async function createTask(taskData: Partial<Task>): Promise<Task> {
  try {
    // Clean the task data to ensure owner_team_id and owner_user_id are not at root level
    const cleanedTaskData: any = { ...taskData }
    
    // Ensure we never send owner_team_id or owner_user_id at the root level
    // These should only exist within the links array
    delete cleanedTaskData.owner_team_id
    delete cleanedTaskData.owner_user_id
    
    const response = await jwtHttpClient.post<CreateTaskResponse>('/v1/tasks', cleanedTaskData, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:tasks'
    })
    
    if (!response.success) {
      throw new Error('Failed to create task')
    }
    
    return response.createdTask || response.task!
  } catch (error) {
    console.error('Failed to create task:', error)
    throw new Error('Failed to create task')
  }
}

export async function searchTasks(body: TaskSearchRequest): Promise<TaskSearchResponse> {
  try {
    const response = await jwtHttpClient.post<TaskSearchResponse>('/v1/tasks/-/search', body, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:inbox',
    })
    return response
  } catch (error) {
    console.error('Failed to search tasks:', error)
    throw new Error('Failed to search tasks')
  }
}

const EMPTY_SEARCH_RESPONSE: TaskSearchResponse = {
  data: [],
  meta: { total: 0, filtered: 0, limit: 0, start: 0, hasMore: false },
}

/**
 * Run two search requests in parallel and merge results, deduplicating by
 * task ID. Used to cover both the rel:user link pattern and the legacy
 * links.owner_user_id pattern until the API normalises them.
 *
 * Result order: primary request first, then any unique tasks from secondary.
 */
export async function searchTasksMerged(
  primary: TaskSearchRequest,
  secondary: TaskSearchRequest,
): Promise<TaskSearchResponse> {
  const [r1, r2] = await Promise.all([
    searchTasks(primary).catch((err) => { console.error('searchTasksMerged primary failed:', err); return EMPTY_SEARCH_RESPONSE }),
    searchTasks(secondary).catch((err) => { console.error('searchTasksMerged secondary failed:', err); return EMPTY_SEARCH_RESPONSE }),
  ])

  const seen = new Set<string>()
  const merged: Task[] = []
  for (const task of [...(r1.data || []), ...(r2.data || [])]) {
    if (!seen.has(task.id)) {
      seen.add(task.id)
      merged.push(task)
    }
  }

  return {
    data: merged,
    meta: {
      total: r1.meta.total + r2.meta.total,
      filtered: merged.length,
      limit: r1.meta.limit,
      start: r1.meta.start,
      hasMore: r1.meta.hasMore || r2.meta.hasMore,
    },
  }
}

export async function fetchAllTasks(orgId: string): Promise<Task[]> {
  try {
    const response = await jwtHttpClient.get<Task[]>('/v1/tasks', { 
      orgId: orgId 
    }, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:mywork'
    })
    return response
  } catch (error) {
    // Check if it's a 404 error (org not found or no tasks)
    if (error instanceof Error && error.message.includes('404')) {
      console.warn(`No tasks found for org ${orgId} (404 - org may not exist or has no tasks)`)
      return [] // Return empty array instead of throwing
    }
    console.error('Failed to fetch all tasks:', error)
    throw new Error('Failed to fetch all tasks')
  }
}

export interface DeleteTaskResponse {
  status: number
  success: boolean
}

export interface BulkEditOwnerRef {
  id: string
}

export interface BulkEditUpdates {
  status?: string
  dueDate?: string | null
  ownerTeam?: BulkEditOwnerRef | null
  ownerUser?: BulkEditOwnerRef | null
}

export interface BulkEditTasksRequest {
  taskIds: string[]
  updates: BulkEditUpdates
}

export interface BulkEditTaskResult {
  id: string
  task?: Task
}

export interface BulkEditTaskFailure {
  id: string
  reason: 'not_found' | 'forbidden' | 'validation_error' | 'update_failed'
  message?: string
}

export interface BulkEditTasksResponse {
  success: boolean
  requested: number
  updated: BulkEditTaskResult[]
  failed: BulkEditTaskFailure[]
}

export async function bulkEditTasks(request: BulkEditTasksRequest): Promise<BulkEditTasksResponse> {
  try {
    return await jwtHttpClient.put<BulkEditTasksResponse>(
      '/v1/tasks',
      request,
      { bulkEdit: 'true' },
      { 'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:inbox' },
    )
  } catch (error) {
    console.error('Failed to bulk edit tasks:', error)
    throw new Error('Failed to bulk edit tasks')
  }
}

export async function deleteTask(taskId: string): Promise<DeleteTaskResponse> {
  try {
    const response = await jwtHttpClient.delete<DeleteTaskResponse>(`/v1/tasks/${encodeURIComponent(taskId)}`, undefined, {
      'x-ovationcxm-source': 'com.ovationcxm.tasks/ui:tasks'
    })
    return response
  } catch (error) {
    console.error('Failed to delete task:', error)
    throw new Error('Failed to delete task')
  }
} 